import Check from "@mui/icons-material/Check";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import WarningAmber from "@mui/icons-material/WarningAmber";
import { Box, Button, Chip, Stack, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { GridRenderCellParams, useGridApiRef } from "@mui/x-data-grid-premium";
import { SubNavAppBar } from "components/AppBar";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { STATE_NAMES } from "encamp-shared/src/constants/states";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { ExtractionStatus, ProcessedManifestsQuery } from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { prettyPrintDaysFromInt } from "util/dates";
import { DAYS_IN_TRANSIT_TOOLTIP_TEXT, getDaysInTransitInfo } from "util/manifest";

import { YearAutocompletePicker } from "../ManifestLineItems/YearAutocompletePIcker";
import { AddManifestButton } from "./AddManifestButton";
import { PROCESSED_MANIFESTS_QUERY } from "./api";
import { DiscrepancyReviewDialog } from "./DiscrepancyReviewDialog";
import { getEManifestComplianceDisplay } from "./eManifestComplianceDisplayV1";
import { ManifestDetails } from "./ManifestDetailsV1";
import { ManifestsExportMenu } from "./ManifestsExportMenuV1";
import { VendorDashboard } from "./VendorDashboard/VendorDashboard";
import { VendorDashboardToggle } from "./VendorDashboard/VendorDashboardToggle";

export type Row = ProcessedManifestsQuery["processedManifests"]["items"][number];

const DOCUMENT_STATES = ["needsReview", "failed", "attached", "approved", "none"] as const;
type DocumentState = (typeof DOCUMENT_STATES)[number];

const DOCUMENT_STATE_LABELS: Record<DocumentState, string> = {
  needsReview: "Needs review",
  failed: "Failed",
  attached: "Attached",
  approved: "Approved",
  none: "None",
};

function getDocumentState(row: Row): DocumentState {
  const sourceDocument = row.sourceDocument;
  if (!sourceDocument) return "none";
  if (sourceDocument.extractionStatus === ExtractionStatus.Failed) return "failed";
  if (sourceDocument.documentMetadata?.needsReview) return "needsReview";
  if (sourceDocument.extractionStatus === ExtractionStatus.Succeeded) return "approved";
  return "attached";
}

export function Manifests() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const persistenceKey = "waste-manifests-v1";
  const minWidth = 120;
  const theme = useTheme();
  const { featureFlags } = useFeatureFlags();
  const { isStaff } = useCurrentUser();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const showDiscrepancyCounts = isStaff && featureFlags[FeatureFlagKey.WasteManifestDiscrepancies];
  const showSourceDocument = isStaff && !!featureFlags[FeatureFlagKey.ManifestDocumentIngest];

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showBulkReviewDialog, setShowBulkReviewDialog] = useState(false);

  const apiRef = useGridApiRef();

  const renderDaysInTransit = useCallback(
    (row: Row): React.ReactNode => {
      const result = getDaysInTransitInfo(row.eManifestDaysInTransit, row.receivedDate, theme);

      if (!result) {
        return "";
      }

      // Always show with color coding based on thresholds
      // Use italic + tooltip if not yet received
      if (!row.receivedDate) {
        return (
          <Tooltip title={result.tooltipText} placement="right" enterDelay={500}>
            <Box component="i" sx={{ color: result.color }}>
              {result.display}
            </Box>
          </Tooltip>
        );
      }

      // Received - show with color but not italic
      return <Box sx={{ color: result.color }}>{result.display}</Box>;
    },
    [theme]
  );

  const { selectedId, pushIdToUrl, popIdFromUrl } = useSelectedIdInUrl("processedManifestId");

  const [shippedDateYear, setShippedDateYear] = useLocalStorageState<number>(
    "waste-manifest-shipped-date-year",
    {
      defaultValue: DateTime.now().year,
    }
  );

  const filename = useMemo(() => `waste-manifests-${shippedDateYear}`, [shippedDateYear]);

  const defaultSearch = useMemo(
    () => `tenantId:${tenantId} shippedDate:${shippedDateYear}`,
    [tenantId, shippedDateYear]
  );
  useBreadcrumb([
    {
      label: "Waste",
      to: tenantId ? `/o/${tenantId}/waste` : undefined,
    },
    {
      label: "Manifests",
    },
  ]);

  const columns: OmnisearchGridColDef<Row>[] = useMemo(
    () => [
      {
        field: "manifestTrackingNumber",
        headerName: "Manifest #",
        minWidth,
        flex: 1,
      },
      ...(showSourceDocument
        ? [
            {
              field: "sourceDocumentState" as const,
              headerName: "Document",
              minWidth: 90,
              flex: 0.3,
              align: "center" as const,
              headerAlign: "center" as const,
              sortable: true,
              filterable: true,
              filterKeyType: "enum" as const,
              enumValues: [...DOCUMENT_STATES],
              enumPresentationFunction: (value: string) => {
                const labels: Record<string, string> = DOCUMENT_STATE_LABELS;
                return labels[value] ?? value;
              },
              valueGetter: (_: unknown, row: Row) => getDocumentState(row),
              renderCell: ({ row }: GridRenderCellParams<Row>) => {
                const state = getDocumentState(row);
                if (state === "none") return null;
                if (state === "needsReview") {
                  return (
                    <Tooltip title="Needs review">
                      <Box component="span" sx={{ fontSize: 16, lineHeight: 1 }}>
                        🔶
                      </Box>
                    </Tooltip>
                  );
                }
                if (state === "failed") {
                  return (
                    <Tooltip title="Manifest import failed — document attached">
                      <ErrorOutline color="error" fontSize="small" />
                    </Tooltip>
                  );
                }
                if (state === "approved") {
                  return (
                    <Tooltip title="Reviewed">
                      <Check color="success" fontSize="small" />
                    </Tooltip>
                  );
                }
                return (
                  <Tooltip title="Document attached">
                    <DescriptionOutlined fontSize="small" color="action" />
                  </Tooltip>
                );
              },
            },
          ]
        : []),
      {
        field: "epaId",
        headerName: "EPA ID",
        minWidth,
        flex: 1,
      },
      {
        field: "facility.name",
        headerName: "Facility",
        filterKeyType: "wasteFacility",
        minWidth,
        flex: 1,
        valueGetter: (_, row) => row.facility?.name ?? "",
      },
      {
        field: "shippedDate",
        headerName: "Ship Date",
        minWidth,
        flex: 1,
        filterKeyType: "date",
        valueFormatter: (value) => {
          return value ? getUtcDisplayDate(value, "med") : null;
        },
      },

      {
        field: "wasteLineCount",
        headerName: "Line Item Count",
        flex: 1,
        minWidth,
      },
      ...(showDiscrepancyCounts
        ? [
            {
              field: "unresolvedDiscrepancyCount" as const,
              headerName: "Unresolved Discrepancies",
              flex: 1,
              minWidth,
              renderHeader: () => (
                <Box fontWeight="medium" sx={{ whiteSpace: "normal", lineHeight: 1.3 }}>
                  Unresolved Discrepancies
                </Box>
              ),
              renderCell: ({ value }: GridRenderCellParams) =>
                value ? (
                  <Chip label={value} size="small" color="warning" variant="outlined" />
                ) : null,
            },
            {
              field: "pendingDiscrepancyCount" as const,
              headerName: "Pending Discrepancies",
              flex: 1,
              minWidth,
              renderHeader: () => (
                <Box fontWeight="medium" sx={{ whiteSpace: "normal", lineHeight: 1.3 }}>
                  Pending Discrepancies
                </Box>
              ),
              renderCell: ({ value }: GridRenderCellParams) =>
                value ? <Chip label={value} size="small" color="info" variant="outlined" /> : null,
            },
          ]
        : []),
      {
        field: "vendor",
        headerName: "Vendor",
        flex: 1,
      },
      {
        field: "isEManifest",
        headerName: "e‑Manifest",
        flex: 1,
        sortable: true,
        ...(showDiscrepancyCounts
          ? {
              filterKeyType: "enum" as const,
              enumValues: ["true", "false", "overdue"],
              enumPresentationFunction: (value: string) => {
                if (value === "true") return "Yes";
                if (value === "false") return "No";
                if (value === "overdue") return "Overdue";
                return value;
              },
            }
          : {
              filterKeyType: "boolean" as const,
            }),
        renderCell: ({ row }) => {
          if (row.isEManifest) {
            return <Check />;
          }

          if (!showDiscrepancyCounts) {
            return null;
          }

          const { tooltipText, severity, actionTaken } = getEManifestComplianceDisplay(row);

          if (severity === "none") {
            return null;
          }

          const Icon = severity === "error" ? ErrorOutline : WarningAmber;
          const iconProps = actionTaken
            ? { color: "disabled" as const }
            : { color: severity as "error" | "warning" };

          return (
            <Tooltip title={tooltipText} placement="right" enterDelay={500}>
              <Icon {...iconProps} />
            </Tooltip>
          );
        },
      },
      {
        // Hidden column only used on the backend for filtering, see columnVisibilityModel below
        field: "isCertified",
        headerName: "Certified",
        flex: 1,
        sortable: true,
        filterKeyType: "boolean",
        renderCell: ({ row }) => {
          if (row.isCertified) {
            return <Check />;
          }

          return null;
        },
      },
      {
        field: "receivedDate",
        headerName: "Received by TSDF",
        minWidth,
        flex: 1,
        filterKeyType: "date",
        renderCell: ({ value, row }) => {
          if (value) {
            return getUtcDisplayDate(value, "med");
          }
          // Only show "Pending" for regulated e-Manifests
          if (row.isRegulatedEManifest) {
            return (
              <Box component="i" sx={{ color: theme.palette.text.secondary }}>
                Pending
              </Box>
            );
          }
          return null;
        },
      },

      {
        field: "eManifestDaysInTransit",
        headerName: "Days In Transit",
        minWidth,
        flex: 1,
        filterKeyType: "number",
        renderHeader: () => (
          <Tooltip title={DAYS_IN_TRANSIT_TOOLTIP_TEXT} placement="top-start" enterDelay={500}>
            <Box fontWeight="medium">Days In Transit</Box>
          </Tooltip>
        ),
        renderCell: ({ row }) => renderDaysInTransit(row),
      },
      ...(showDiscrepancyCounts
        ? [
            {
              field: "daysSinceShipment" as const,
              headerName: "Days Awaiting e-Manifest",
              minWidth,
              flex: 1,
              filterKeyType: "number" as const,
              sortable: true,
              renderHeader: () => (
                <Tooltip
                  title="Days since shipment for hazardous waste manifests on a uniform tracking number that are still awaiting an e-Manifest record. EPA requires contacting the transporter at 45 days and filing an exception report at 60 days."
                  placement="top-start"
                  enterDelay={500}
                >
                  <Box fontWeight="medium" sx={{ whiteSpace: "normal", lineHeight: 1.3 }}>
                    Days Awaiting{" "}
                    <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                      e-Manifest
                    </Box>
                  </Box>
                </Tooltip>
              ),
              renderCell: ({ row }: GridRenderCellParams<Row>) => {
                if (row.isEManifest || !row.isUniformHazardousManifest) {
                  return null;
                }
                const days = row.daysSinceShipment;
                if (days == null) {
                  return null;
                }
                const { tooltipText, severity } = getEManifestComplianceDisplay(row);
                const colorBySeverity: Record<string, string | undefined> = {
                  error: theme.palette.error.main,
                  warning: theme.palette.warning.main,
                };
                const color = colorBySeverity[severity];
                return (
                  <Tooltip title={tooltipText} placement="right" enterDelay={500}>
                    <Box component={severity !== "none" ? "i" : "span"} sx={{ color }}>
                      {prettyPrintDaysFromInt(days)}
                    </Box>
                  </Tooltip>
                );
              },
            },
          ]
        : []),
    ],
    [renderDaysInTransit, showDiscrepancyCounts, showSourceDocument, theme]
  );

  return (
    <Box>
      <SubNavAppBar
        sx={{
          py: 2,
          px: 3,
          borderBottom: "1px solid",
          borderColor: theme.palette.divider,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <Box sx={{ width: 80 }}>
            <YearAutocompletePicker
              label="Year"
              minYear={2018}
              value={shippedDateYear}
              onChange={setShippedDateYear}
            />
          </Box>
          <VendorDashboardToggle />
        </Stack>
      </SubNavAppBar>
      <VendorDashboard shippedDateYear={shippedDateYear} />
      <Box py={3}>
        <OmnisearchDataGrid
          apiRef={apiRef}
          persistenceKey={persistenceKey}
          isURLDriven
          columns={columns}
          dataQuery={PROCESSED_MANIFESTS_QUERY}
          getItems={(data) => data.processedManifests.items}
          getCount={(data) => data.processedManifests.count}
          noDataMessage={`No manifests have been added to this organization.`}
          checkboxSelection={showDiscrepancyCounts}
          onSelectedIdsChanged={
            showDiscrepancyCounts ? (ids) => setSelectedIds(ids.map(String)) : undefined
          }
          disableRowSelectionOnClick
          onRowClick={({ row: { id } }) => pushIdToUrl(id)}
          defaultSearch={defaultSearch}
          initialSortModel={[
            ...(showSourceDocument ? [{ field: "sourceDocumentState", sort: "asc" as const }] : []),
            { field: "shippedDate", sort: "desc" },
          ]}
          rowHeight={"auto"}
          commandButtons={({ search }) => [
            ...(showDiscrepancyCounts && selectedIds.length > 0
              ? [
                  <Button
                    key="review-discrepancies"
                    variant="outlined"
                    color="warning"
                    startIcon={<CompareArrowsIcon />}
                    onClick={() => setShowBulkReviewDialog(true)}
                  >
                    Review Discrepancies ({selectedIds.length})
                  </Button>,
                ]
              : []),
            <AddManifestButton key="add-manifest" tenantId={tenantId ?? ""} />,
            <ManifestsExportMenu
              key="export"
              tenantId={tenantId ?? ""}
              fileName={filename}
              search={search}
              selectedIds={selectedIds}
              showDiscrepancyCounts={showDiscrepancyCounts}
            />,
          ]}
          showFilters
          additionalFilterColumns={[
            {
              header: "State",
              key: "states",
              filterKeyType: "enumAutocomplete",
              enumValues: Object.keys(STATE_NAMES),
              enumPresentationFunction: (value: string) => {
                const name = STATE_NAMES[value as keyof typeof STATE_NAMES];
                return name ? `${name} (${value})` : value;
              },
            },
          ]}
          excludeFilterColumns={["wasteLineCount"]}
          columnVisibilityModel={{
            isCertified: false,
            ...(showDiscrepancyCounts && {
              receivedDate: false,
              eManifestDaysInTransit: false,
            }),
            ...(isMobile && {
              epaId: false,
              wasteLines: false,
              amount: false,
              wasteProfileNumber: false,
              managementMethodCode: false,
              vendor: false,
              eManifest: false,
              vendorManifest: false,
            }),
          }}
        />
      </Box>
      <ManifestDetails
        processedManifestId={selectedId}
        open={!!selectedId}
        onClose={popIdFromUrl}
      />
      {showBulkReviewDialog && selectedIds.length > 0 && (
        <DiscrepancyReviewDialog
          processedManifestIds={selectedIds}
          onClose={() => setShowBulkReviewDialog(false)}
        />
      )}
    </Box>
  );
}
